import React, { useState, useCallback } from "react";
import classnames from "classnames";
import Avatar from "react-avatar";
import { useIntl } from "react-intl";
import DropDown from "./DropDown";
import MyClickAwayListener from "../../../components/ClickAwayListener";
import { getRoleLabel, getRole } from "../../../utils/user";
import { UserCognito } from "../../../lib/ground-aws-cognito-auth-core/models/Authentication";
import ChangeLogWidget from "../../../components/ChangeLog";
import { getChangeLogWidgetKey } from "../../../utils/config";
import images from "../../../images";

interface Props {
  user: UserCognito;
  className?: string;
}

const NavUser = ({ user, className }: Props): JSX.Element => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const toggleDropdown = useCallback(() => setOpen(prev => !prev), []);

  const userRole = getRole(user.role);
  const roleLabel = userRole ? getRoleLabel(intl, userRole) : user.role;

  return (
    <div className={classnames("relative flex-shrink-0 z-50", className)}>
      <div className="flex items-center">
        <div className="onesignal-customlink-container flex items-center" />

        {getChangeLogWidgetKey() && <ChangeLogWidget />}

        <AvatarDropdown
          user={user}
          open={open}
          toggleDropdown={toggleDropdown}
          roleLabel={roleLabel}
        />
      </div>
    </div>
  );
};

interface AvatarDropdownProps {
  user: UserCognito;
  open: boolean;
  toggleDropdown: () => void;
  roleLabel: string;
}

const AvatarDropdown = ({
  user,
  open,
  toggleDropdown,
  roleLabel,
}: AvatarDropdownProps) => (
  <div
    id="btn-user-avatar"
    data-cy="btn-user-avatar"
    onClick={toggleDropdown}
    className="flex items-center cursor-pointer rounded-full border-2 border-transparent ml-3 transition duration-150 ease-in-out focus:outline-none focus:border-gray-300"
  >
    <Avatar
      name={`${user.first_name} ${user.last_name}`}
      size="40"
      round
      color="#898E9A"
    />
    <div className="flex flex-col ml-2 items-end">
      <p className="text-ground-white-100 text-12px">
        {user.first_name} {user.last_name}
      </p>
      <p className="text-ground-green-100 text-10px">{roleLabel}</p>
    </div>
    <img src={images.bottomArrow} alt="dropdown" className="w-8 h-8 ml-2" />

    {open && (
      <MyClickAwayListener
        item={{ id: user.id }}
        id="click-away-user"
        onClickAway={() => toggleDropdown()}
      >
        <DropDown open={open} user={user} />
      </MyClickAwayListener>
    )}
  </div>
);

export default NavUser;
